import {Component} from "react";
import {
    defineSpecificUrl,
    formatConfig,
    getConfigAsync,
    getConfigUrl,
    getPlayerImage,
    isAuthorized,
    getTimelineAsync,
    isTemplateConfig, getStreamFromConfig
} from "../../helpers/configHelper";
import AppConfig from "../../config/config.json";
import {activeSpectrum, createAudioMotionSpectrum} from "../../helpers/spectrumHelper";
import {updateMediaSession} from "../../helpers/mediaSessionHelper";
import Hls from "hls.js";
import {getIframeWindowFromParent, isTrustedMessageOrigin} from "../../helpers/windowHelper";
import {initPlayerColors} from "../../helpers/rendererHelper";

export const numberFragmentBeforeLive = 3;
export default class Player extends Component {
    titleToDisplay = null;
    authorToDisplay = null;
    imageToDisplay = null;
    audioMotion = null;
    queue = [];
    getConfigIntervalFunction = null;
    reduceHlsTimeIntervalFunction = null;
    isChangingImage = false;
    hlsPlayer = null;

    /* ------------------------------------------------------------

                    Init Functions

    ------------------------------------------------------------ */

    constructor(props) {
        super(props);

        if (AppConfig.env !== 'prod') {
            window.IPS.config = {
                url: 'ws://preprod-info-mq.infomaniak.com',
                user: 'ips:ips-public',
                pass: '4fBt5AdC2P'
            };
        }

        this.state = {
            autoplay: !!parseInt(props.autoplay),
            configId: props.configId,
            configUrl: getConfigUrl(props),
            controls: false,
            error: false,
            isExtend: false,
            light: false,
            loaded: 0,
            locale: props.locale,
            muted: false,
            neverPlayed: true,
            playbackRate: 1.0,
            played: 0,
            playing: false,
            preview: props.preview,
            bufferPreviewed: props.bufferPreviewed,
            onChangeCommittedFlush: props.onChangeCommittedFlush,
            stationIndex: 0,
            stream: null,
            timeline: [],
            tokens: props.tokens,
            urlParam: props.url,
            previousPlayTime: 0,
            splitHistoryRatio: props.splitHistoryRatio,
            bufferDuration: null, // real duration of the buffer given by the hls player (may differ from fragment size * playlist size)
            timelineLoaded: false,
        }
    }

    async componentDidMount() {
        let config = await getConfigAsync(this.state.configUrl);

        if (!config) {
            window.location.href = `${AppConfig.basename}/404`;
            return;
        }

        window.addEventListener("offline", () => {
            this.setState({wasOffline: true});
        });

        window.addEventListener("online", () => {
            if (this.state.wasOffline) {
                if (this.hlsPlayer) {
                    // Used to flush the buffer
                    this.hlsPlayer.trigger(Hls.Events.BUFFER_FLUSHING, {
                        startOffset: 0,
                        endOffset: Infinity,
                        type: 'audio'
                    });
                    this.hlsPlayer.startLoad();
                }
                this.subscribeRPSForNewMetadata();
                this.subscribeRPSForListener();
            }
        });

        // Override config
        // Create the player with a fake url for not load the real one and do a hit in statistics
        config.url = 'temp.mp3';
        if (this.state.urlParam) {
            config = defineSpecificUrl(this.state.urlParam, config);
        }

        this.init(config, () => {
            const stationId = this.state.stations[this.state.stationIndex]?.id;

            if (Boolean(stationId)) {
                // after the config is initialized, fetch the timeline
                getTimelineAsync(stationId).then((timeline) => {
                    // check if the station hasn't changed when receiving the new timeline
                    if (stationId === this.state.stations[this.state.stationIndex]?.id) {
                        this.setState({timeline, timelineLoaded: true}, () => {
                            // update the split history config after state change
                            this.emitConfigToSplitHistory(this.state);

                            this.subscribeRPSForNewMetadata();
                            this.subscribeRPSForListener();
                        });
                    }
                });
            }
        });

        if (!this.state.error && !this.state.urlParam) {
            // init message listeners
            if (this.state.preview || config.history_display === 'split') {
                this.addMessageListener();
            }
        }
    }

    init = (config, callback, isPreview = false) => {
        let configFormatted = formatConfig(config, this.state, isPreview);

        // Define a volume temp for come back to the initial value
        configFormatted.volumeTemp = configFormatted.volume;

        configFormatted.title = this.state.title || configFormatted.playername;
        configFormatted.author = this.state.author ?? '';

        initPlayerColors(configFormatted);
        this.setState(configFormatted, callback);
    }

    // Call at the creation or after load function
    // The config is not change here
    onPlayerReady = () => {
        // To announce availability to parent window
        window.top.postMessage('player-ready', '*')

        if (!this.state.error && !this.state.neverPlayed && this.state.stream.format === 'hls') {
            const isHlsPlayerFirstInit = !this.hlsPlayer;

            // Get the hls player, it's undefined if the stream is not a HLS stream
            this.hlsPlayer = this.player?.getInternalPlayer('hls');

            // on the first init of the hls player, start loading at the correct position depending on the buffer mode
            if (isHlsPlayerFirstInit && this.hlsPlayer && this.state.isHlsBuffered) {
                this.hlsPlayer.loadLevel = 1;
                this.hlsPlayer.startLoad(this.state.fragment * numberFragmentBeforeLive);
            }
        }

        if (!this.state.preview && this.state.neverPlayed && this.state.autoplay) {
            this.handlePlay();
        }
    }

    /* ------------------------------------------------------------

                        Major Functions

    ------------------------------------------------------------ */

    emitConfigToSplitHistory = (config) => {
        if (config.historyDisplay === 'split' || this.state.historyDisplay === 'split') {
            const splitHistoryRegex = /\/(?:prod|template)\/history/
            const splitHistoryWindow = getIframeWindowFromParent(splitHistoryRegex);

            if (splitHistoryWindow) {
                splitHistoryWindow.postMessage(JSON.stringify({
                    ...config,
                    uuid: this.state.configId,
                }), window.origin);
            }
        }
    }

    // Call when user changes the quality
    load = (stream, isPreview = false) => {
        if (stream.url !== this.state.url) {
            if (!isPreview) {
                this.isLoading = true;
                // Apply a loading image for the transition between streams
                this.changeImageAndTitleAuthor(this.state.loadingUrl, '', '');
            }

            if (this.state.playing) {
                // Stop playing to remove stream loading
                this.handlePause();
            }

            // Error if the user is unauthorized
            if (!isTemplateConfig(this.state.configUrl) && !isAuthorized(stream)) {
                // change stream for display (like option selected)
                this.setState({stream: stream});
                this.setPlayerInError(this.state.unauthorizedUrl, this.state);
            } else {
                // Set to the player the new stream url and define stream is played
                // Reset the error status when the player listen another stream
                this.setState({url: stream.url, stream: stream, error: false}, function () {
                    // Create spectrum in loading, not in ready state because is useless to create a spectrum on the same stream
                    if (this.state.spectrum && !isPreview) {
                        createAudioMotionSpectrum(this);
                    }

                    // Force to play when user loads a new stream
                    if (!this.state.error && (this.state.playing || !isPreview)) {
                        this.handlePlay();
                    }
                });
            }

            this.emitConfigToSplitHistory({
                ...this.state,
                currentTrack: this.state.title,
            });

            this.isLoading = false;
        }
    }

    handleSeekTo = (value, isCommitted) => {
        if (this.hlsPlayer) {
            const previousPlayTime = value - this.state.bufferDuration;
            this.setState({ previousPlayTime });

            if (this.hlsPlayer.url) {
                if (isCommitted) this.player.seekTo(this.player.getDuration() + previousPlayTime);
            }
        }
    }


    // Call every second when the player is playing and if the stream is up
    handleProgress = state => {
        const playerBufferValue = Math.round(this.hlsPlayer?.bufferController?.details?.totalduration) || null;
        const timeline = this.state.timeline;
        let previousPlayTime = this.state.previousPlayTime;

        let timeLineLastElementIndex = (timeline?.length || 1) - 1;
        let currentTimelineElement = timeline[timeLineLastElementIndex];

        if (this.state.stream.format === 'hls') {
            // if the buffer is not fully loaded in big buffer and the previous playtime is outside the buffer
            // reinitialize the previous play time to get the real value
            if (playerBufferValue < this.state.fragment * this.state.playlist && playerBufferValue + this.state.previousPlayTime < 0 && this.state.isHlsBuffered) {
                previousPlayTime = this.state.fragment * numberFragmentBeforeLive - playerBufferValue;
                state.previousPlayTime = previousPlayTime;
            }

            const currentPlay = ((Date.now() + (previousPlayTime * 1000)) / 1000);
            let bufferDuration = Math.min(playerBufferValue, this.state.fragment * this.state.playlist);
            let index = 0;

            if (timeline) {
                while (index < timeline.length && currentPlay > (timeline[index].date) + this.state.fragment * numberFragmentBeforeLive) {
                    currentTimelineElement = timeline[index];
                    index++;
                }
            }
            // when isHlsBuffered active and near of the end of it flushing and reloading the source
            if (this.state.isHlsBuffered && this.player.getCurrentTime() + this.state.fragment * numberFragmentBeforeLive > this.player.getSecondsLoaded()) {
                this.hlsPlayer?.trigger(Hls.Events.BUFFER_FLUSHING, {
                    startOffset: 0,
                    endOffset: this.player.getSecondsLoaded(),
                    type: 'audio'
                });
                this.hlsPlayer?.startLoad(this.state.fragment * numberFragmentBeforeLive);
            }

            // when the buffer is not fully loaded (after a hls restart for example)
            // if a progress event occur and the buffer hasn't been updated, increment it manually of 1s
            // (the buffer is updated at every fragment which is not accurate enough)
            if (this.state.bufferDuration && bufferDuration < (this.state.fragment * this.state.playlist) && bufferDuration <= this.state.bufferDuration) {
                bufferDuration = this.state.bufferDuration + 1;
            }

            if (this.state.bufferDuration !== bufferDuration) {
                // emit the buffer to the split history if the value change
                this.emitConfigToSplitHistory({ bufferSize: bufferDuration });
            }

            this.setState({ bufferDuration });
        }

        this.titleToDisplay = currentTimelineElement?.title || this.state.playername || '';
        this.authorToDisplay = currentTimelineElement?.author ?? '';
        this.imageToDisplay = getPlayerImage(this.state, currentTimelineElement);

        this.changeImageAndTitleAuthor(this.imageToDisplay, this.titleToDisplay, this.authorToDisplay);
        this.setState(state);
    }

    handlePlay = () => {
        // First play
        if (this.state.neverPlayed) {
            // Apply a background-color transition after the first play to not trigger a transition at the loading page
            document.body.style.transition = 'background-color 1s';
            this.setState({neverPlayed: false});
        }
        if (!isTemplateConfig(this.state.configUrl) && !isAuthorized(this.state.stream)) {
            this.setPlayerInError(this.state.unauthorizedUrl);
        } else if (!this.state.error) {
            // Start loading
            if (this.state.stream.format === 'hls') {
                // reload HLS stream at the end
                this.setState({url: this.state.stream.url}, () => {
                    const liveOffset = this.state.fragment * numberFragmentBeforeLive;
                    const bufferDuration = this.state.bufferDuration ?? this.state.fragment * this.state.playlist;
                    let previousPlayTime;

                    // Create spectrum
                    if (this.state.spectrum) {
                        createAudioMotionSpectrum(this);
                    }

                    if (this.state.isHlsBuffered) {
                        // on big buffer play from the first available fragment available in the buffer + live offset
                        this.player.seekTo(liveOffset);
                        previousPlayTime = liveOffset - bufferDuration;
                    } else if (this.state.isDirectControl && this.state.previousPlayTime + liveOffset <= 0) {
                        // on direct control, play where the player stopped for the last time
                        this.player.seekTo(bufferDuration + this.state.previousPlayTime - 1);
                        previousPlayTime = this.state.previousPlayTime;
                    } else {
                        // else seek to live - 15s
                        this.player.seekTo(bufferDuration - liveOffset);
                        previousPlayTime = -liveOffset;
                    }

                    this.setState({previousPlayTime});
                });
            } else {
                this.setState({ previousPlayTime: -this.state.fragment * numberFragmentBeforeLive});

                this.player.player.player.player.src = this.state.stream.url;
                this.player.player.player.player.load();
                if (this.state.spectrum) {
                    createAudioMotionSpectrum(this);
                }
            }

            clearInterval(this.reduceHlsTimeIntervalFunction);
            this.reduceHlsTimeIntervalFunction = null;

            // With url in the param this function doesn't exist
            if (this.getConfigIntervalFunction) {
                // Force to get the last config
                this.getConfigIntervalFunction(true);
            }
        }
        this.setState({playing: true}, () => this.emitConfigToSplitHistory(this.state));
        window.top.postMessage('playing', '*');
    }

    handlePause = () => {
        // Stop loading
        if (this.state.stream.format === 'hls' && this.hlsPlayer) {
            this.hlsPlayer.stopLoad();
            // change the stream url for reload the right stream at the play
            this.setState({url: ''});
            this.reduceHlsTimeIntervalFunction = setInterval(() => {
                const bufferLoaded = this.state.bufferDuration === this.state.fragment * this.state.playlist; // if the player buffer is fully loaded

                this.setState({
                    previousPlayTime: this.state.previousPlayTime - 1,
                    bufferDuration: this.state.bufferDuration + (bufferLoaded ? 0 : 1),// if the buffer is not loaded, increase every 1s when paused to keep an accurate value
                })
            }, 1000);

        } else {
            // Empty the src player to stop loading
            this.player.player.player.player.src = '';
            this.player.player.player.player.load();
        }
        this.setState({playing: false}, () => this.emitConfigToSplitHistory(this.state));
        window.top.postMessage('paused', '*');
    }


    changeImageAndTitleAuthor = (url, title, author = '') => {
        if (!this.state.is_metadata_display) {
            title = this.state.playername;
            author = '';
        }

        const imageHTML = document.getElementById('image');
        let spectrumHTML = document.getElementById('spectrum');
        const titleHTML = document.getElementById('title-container');
        const authorHTML = document.getElementById('author-container');
        if (this.isChangingImage === false) {
            if (url !== this.state.display || title !== this.state.title || author !== this.state.author) {
                this.isChangingImage = true;
                if (spectrumHTML && (url !== this.state.display || !this.state.spectrum) && !activeSpectrum(this, url)) {
                    spectrumHTML.classList.add("fadeOut");
                }

                if ((url !== this.state.display || activeSpectrum(this)) && !this.state.neverPlayed) {
                    imageHTML?.classList?.add("fadeOut");
                }

                if (!title) {
                    if (author) {
                        title = author;
                        author = '';
                    }
                }

                if (this.state.title !== title) {
                    titleHTML.classList.add("fadeOut");
                }

                if (this.state.author !== author) {
                    authorHTML.classList.add("fadeOut");
                }

                this.emitConfigToSplitHistory({
                    ...this.state,
                    currentTrack: title,
                });

                let player = this;
                // Wait the fadeOut end
                setTimeout(function () {
                    let previousUrl = player.state.display;
                    player.setState({display: url, title: title, author: author}, function () {
                        player.handleMarquee('title-container');
                        player.handleMarquee('author-container');
                        if (previousUrl === url) {
                            spectrumHTML = document.getElementById('spectrum');
                            if (activeSpectrum(player) && spectrumHTML) {
                                spectrumHTML.classList.remove("fadeOut");
                            }
                        }
                        if (url && url.split('.').pop() === 'svg') {
                            player.onLoadImage();
                        }
                        titleHTML.classList.remove("fadeOut");
                        authorHTML.classList.remove("fadeOut");
                        // Wait the fadeIn end
                        setTimeout(function () {
                            player.isChangingImage = false;
                            updateMediaSession(player);
                            if (player.queue.length > 0) {
                                let next = player.queue.shift();
                                player.changeImageAndTitleAuthor(next.url, next.title, next.author);
                            }
                        }, 1000);
                    });
                }, 1100);
            } else if (this.queue.length > 0) {
                let next = this.queue.shift();
                this.changeImageAndTitleAuthor(next.url, next.title, next.author);
            }
        } else if (!(this.queue.find((element) => element.url === url && element.title === title && element.author === author))) {
            this.queue.push({
                url: url,
                title: title,
                author: author
            })
        }
    }

    setPlayerInError = (imageToDisplay, config = this.state) => {
        if (!config.error || this.isLoading) {
            this.setState({error: true, url: "temp.mp3"});
            this.queue = [];
            this.changeImageAndTitleAuthor(imageToDisplay, config.translations.error);
        }
    }

    subscribeRPSForNewMetadata = () => {
        if (window.IPS) {
            try {
                window.IPS.subscribe(
                    'radio', {'uid': 'metadata/' + this.state.stations[this.state.stationIndex].id + '/new_metadata'},
                    (res, mixed) => {
                        if (res === true) {
                            mixed.on('radio', data => {
                                const string = String.fromCharCode.apply(String, data);
                                try {
                                    const newMetadata = JSON.parse(string);

                                    const splitTitle = newMetadata.title.replace('˗', '-').split(' - ');
                                    const metadata = {
                                        ...newMetadata,
                                        author: splitTitle.shift(),
                                        title: splitTitle.join(' - '),
                                    }

                                    this.setState({'timeline': [...this.state.timeline, metadata]});
                                } catch (error) {
                                    console.log(error)
                                }
                            });
                        } else {
                            console.log("WS subscribe fail for last metadata");
                        }
                    }
                );
            } catch (err) {
                console.log("WS subscribe fail for last metadata");
            }
        }
    }

    unsubscribeRPSForNewMetadata = () => {
        if (window.IPS) {
            window.IPS.unsubscribe('radio', {'uid': 'metadata/' + this.state.stations[this.state.stationIndex].id + '/new_metadata'});
        }
    }

    subscribeRPSForListener = () => {
        if (window.IPS && this.state.displayListener) {
            try {
                window.IPS.subscribe(
                    'radio', {'uid': 'station/' + this.state.stations[this.state.stationIndex].id + '/listener'},
                    (res, mixed) => {
                        if (res === true) {
                            mixed.on('radio', data => {
                                const string = String.fromCharCode.apply(String, data);
                                try {
                                    this.setState({numberListener: JSON.parse(string)});
                                } catch (error) {
                                    console.log(error);
                                }
                            });
                        } else {
                            console.log("WS subscribe fail for listener");
                        }
                    }
                );
            } catch (err) {
                console.log("WS subscribe fail for listener");
            }
        }
    }

    unsubscribeRPSForListener = () => {
        if (window.IPS && this.state.displayListener) {
            window.IPS.unsubscribe('radio', {'uid': 'station/' + this.state.stations[this.state.stationIndex].id + '/listener'});
        }
    }


    addMessageListener = () => {
        window.addEventListener('message', event => {
            // only accept messages from the player itself (split history) and the manager (preview)
            if (!isTrustedMessageOrigin(event.origin)) {
                return;
            }

            let newConfig;
            try {
                newConfig = JSON.parse(event.data);
            } catch (e) {
                return;
            }

            // if the event is a seek event, seek to the corresponding timestamp
            if ('seekTo' in newConfig) {
                this.handleSeekTo(newConfig.seekTo, true);
            } else {
                // compare if 2 arrays are identical by a specific key, ex: 'id' or 'uuid'
                const compareArraysByKey = (firstArray, secondArray, key) => firstArray?.length === secondArray?.length && firstArray?.every((item, index) => item[key] === secondArray[index][key]);

                // check if the streams and stations lists have been updated
                const streamListUpdated = !compareArraysByKey(this.state.streams, newConfig.stations.flatMap((station) => station.streams), 'uuid');
                const stationListUpdated = !compareArraysByKey(this.state.stations, newConfig.stations, 'id');

                // if the stations list has changed in preview, open the super player extended panel
                if (this.state.preview && newConfig.type <= 2 && stationListUpdated) {
                    this.state.isExtend = true;
                }

                // init the player with the new config
                this.init({...this.state, ...newConfig},() => {
                    // update the split history config after state change
                    this.emitConfigToSplitHistory(this.state);

                    // update the current stream on preview if the streams list is updated
                    if (this.state.preview && streamListUpdated) {
                        const mainStationIndex = this.state.stations.findIndex((station) => station.is_main);

                        this.handleChangeStation(mainStationIndex, true);
                    } else {
                        const currentTrack =  this.state.timeline.find((track) => track.author === this.authorToDisplay && track.title === this.titleToDisplay);

                        this.imageToDisplay = getPlayerImage({...this.state, ...newConfig}, currentTrack);
                        this.changeImageAndTitleAuthor(this.imageToDisplay, this.titleToDisplay, this.authorToDisplay);
                    }

                    if (this.state.neverPlayed && this.state.autoplay) {
                        this.handlePlay();
                    }
                }, true);
            }
        });
    }

    // switch to the station corresponding to the given index
    handleChangeStation = (index, isPreview = false) => {
        // If the stream id change
        if (index !== -1 && this.state.stream.uuid !== this.state.stations[index]?.streams[0].uuid) {
            const streams = getStreamFromConfig(this.state, {stationIndex: index, neverPlayed: this.state.neverPlayed});

            this.handlePause();

            // close all the overlays
            this.handleCloseOverlay('quality');
            this.handleCloseOverlay('share');
            this.state.isExtend = false;

            // unsubscribe from the last station metadata
            this.unsubscribeRPSForNewMetadata();
            this.unsubscribeRPSForListener();

            this.setState({
                ...streams,
                bufferDuration: streams.fragment * streams.playlist,
                timeline: [], // set timeline to null to null while loading the new metadata
                timelineLoaded: false,
                stationIndex: index,
            }, () => {
                const stationId = this.state.stations[this.state.stationIndex]?.id;

                getTimelineAsync(stationId).then((timeline) => {
                    // check if the station hasn't changed when receiving the new timeline
                    if (stationId === this.state.stations[this.state.stationIndex]?.id) {
                        this.setState({timeline, timelineLoaded: true}, () => {
                            // update the split history config after state change
                            this.emitConfigToSplitHistory(this.state);

                            this.subscribeRPSForNewMetadata();
                            this.subscribeRPSForListener();
                        });
                    }
                });

                // reset the previous playTime
                if (this.state.fragment && this.state.playlist) {
                    const liveOffset = this.state.fragment * numberFragmentBeforeLive;
                    const bufferDuration = this.state.fragment * this.state.playlist;

                    this.setState({
                        previousPlayTime: this.state.isHlsBuffered ? (liveOffset - bufferDuration) : (-liveOffset),
                    });
                }

                this.queue = [];
                this.load(streams.main, isPreview);

                if (this.state.neverPlayed && !isPreview) {
                    // Apply a background-color transition after the first play to not trigger a transition at the loading page
                    document.body.style.transition = 'background-color 1s';
                    this.setState({neverPlayed: false});
                }
            });
        }
    }

    /* ------------------------------------------------------------

                            Minor Functions

    ------------------------------------------------------------ */

    handleToggleExtend = () => {
        this.setState({isExtend: !this.state.isExtend});
    }

    handleOpenOverlay = overlayName => {
        const overlayHTML = document.getElementById(overlayName + "-overlay");
        if (overlayHTML) {
            overlayHTML.classList.add("show-overlay");
        }
    }

    handleCloseOverlay = overlayName => {
        const overlayHTML = document.getElementById(overlayName + "-overlay");
        if (overlayHTML) {
            overlayHTML.classList.remove("show-overlay");
        }
    }

    handleVolumeChange = (e, value) => {
        this.setState({muted: value === 0, volume: parseFloat(value)});
    }

    handleToggleMuted = () => {
        const isMuted = this.state.muted;
        if (!isMuted) {
            this.setState({volumeTemp: this.state.volume});
            this.handleVolumeChange(null, 0);
        } else {
            this.handleVolumeChange(null, this.state.volumeTemp);
        }
        this.setState({muted: !isMuted});
    }

    // handleMarquee is call in changeImageAndTitleAuthor
    handleMarquee = idElement => {
        const element = document.getElementById(idElement);
        if (element.offsetWidth < element.scrollWidth) {
            element.classList.add("marquee");
        } else {
            element.classList.remove("marquee");
        }
    }

    onLoadImage = () => {
        let imageHTML = document.getElementById('image');
        let spectrumHTML = document.getElementById('spectrum');
        if (!activeSpectrum(this) || !spectrumHTML) {
            imageHTML?.classList?.remove("fadeOut");
            spectrumHTML?.classList?.add("fadeOut");
        } else {
            spectrumHTML?.classList?.remove("fadeOut");
            imageHTML?.classList?.add("fadeOut");
        }
    }

    ref = player => {
        this.player = player;
    }
}
